import React, {
  useEffect,
  useRef,
  forwardRef,
  useImperativeHandle,
} from 'react'
import * as echarts from 'echarts'
import { safeChartInit } from '../../../../../utils/echarts'
import type { TFunction } from 'i18next'
import { Icon } from '../../../../atoms/Icon'

interface SectorCompositionChartProps {
  sector1: number
  sector2: number
  sector3: number
  height?: number
  t: TFunction
}

export interface SectorCompositionChartRef {
  exportChart: () => string | null
}

export const SectorCompositionChart = forwardRef<
  SectorCompositionChartRef,
  SectorCompositionChartProps
>(({ sector1, sector2, sector3, height = 400, t }, ref) => {
  const chartRef = useRef<HTMLDivElement>(null)
  const chartInstance = useRef<echarts.ECharts | null>(null)

  useImperativeHandle(ref, () => ({
    exportChart: () => {
      if (chartInstance.current) {
        return chartInstance.current.getDataURL({
          type: 'png',
          pixelRatio: 2,
          backgroundColor: '#fff',
        })
      }
      return null
    },
  }))

  useEffect(() => {
    if (!chartRef.current) return

    // Initialize chart
    chartInstance.current = safeChartInit(chartRef.current)

    const total = sector1 + sector2 + sector3

    if (total === 0) {
      const option: echarts.EChartsOption = {
        title: {
          text: t('charts.sectorComposition.noData'),
          left: 'center',
          top: 'middle',
          textStyle: {
            color: '#9ca3af',
            fontSize: 14,
          },
        },
      }
      chartInstance.current.setOption(option)
      return
    }

    const data = [
      {
        value: sector1,
        name: t('sectors.primary.name'),
        itemStyle: { color: '#f59e0b' },
      },
      {
        value: sector2,
        name: t('sectors.secondary.name'),
        itemStyle: { color: '#3b82f6' },
      },
      {
        value: sector3,
        name: t('sectors.tertiary.name'),
        itemStyle: { color: '#10b981' },
      },
    ]

    const option: echarts.EChartsOption = {
      tooltip: {
        trigger: 'item',
        formatter: function (params: any) {
          const percentage = ((params.value / total) * 100).toFixed(1)
          return `
            <div style="text-align: left;">
              <strong>${params.name}</strong><br/>
              ${t('charts.sectorComposition.tooltips.jobs')}: ${params.value.toLocaleString()}<br/>
              ${t('charts.sectorComposition.tooltips.share')}: ${percentage}%
            </div>
          `
        },
      },
      legend: {
        orient: 'vertical',
        right: '10%',
        top: 'center',
        textStyle: {
          fontSize: 11,
        },
        formatter: function (name: string) {
          const item = data.find((d) => d.name === name)
          if (!item) return name
          const percentage = ((item.value / total) * 100).toFixed(1)
          // Get short name based on the full name
          let shortName = name.split(' ')[0]
          if (name === t('sectors.primary.name'))
            shortName = t('sectors.primary.short')
          else if (name === t('sectors.secondary.name'))
            shortName = t('sectors.secondary.short')
          else if (name === t('sectors.tertiary.name'))
            shortName = t('sectors.tertiary.short')
          return `${shortName}: ${percentage}%`
        },
      },
      series: [
        {
          name: t('charts.sectorComposition.title'),
          type: 'pie',
          radius: ['40%', '70%'],
          center: ['35%', '50%'],
          avoidLabelOverlap: false,
          itemStyle: {
            borderRadius: 10,
            borderColor: '#fff',
            borderWidth: 2,
          },
          label: {
            show: false,
            position: 'center',
          },
          emphasis: {
            label: {
              show: true,
              fontSize: 16,
              fontWeight: 'bold',
              formatter: function (params: any) {
                const percentage = ((params.value / total) * 100).toFixed(1)
                let shortName = params.name.split(' ')[0]
                if (params.name === t('sectors.primary.name'))
                  shortName = t('sectors.primary.short')
                else if (params.name === t('sectors.secondary.name'))
                  shortName = t('sectors.secondary.short')
                else if (params.name === t('sectors.tertiary.name'))
                  shortName = t('sectors.tertiary.short')
                return `${shortName}\n${percentage}%`
              },
            },
          },
          labelLine: {
            show: false,
          },
          data: data,
        },
      ],
    }

    chartInstance.current.setOption(option)

    // Handle resize
    const handleResize = () => {
      chartInstance.current?.resize()
    }

    window.addEventListener('resize', handleResize)

    return () => {
      window.removeEventListener('resize', handleResize)
      chartInstance.current?.dispose()
    }
  }, [sector1, sector2, sector3, height, t])

  if (sector1 + sector2 + sector3 === 0) {
    return (
      <div
        style={{
          height: `${height}px`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#6b7280',
        }}
      >
        {t('charts.sectorComposition.noData')}
      </div>
    )
  }

  const total = sector1 + sector2 + sector3
  const dominantSector =
    sector1 > sector2 && sector1 > sector3
      ? t('sectors.primary.short')
      : sector2 > sector3
        ? t('sectors.secondary.short')
        : t('sectors.tertiary.short')

  return (
    <div>
      <div
        ref={chartRef}
        style={{
          width: '100%',
          height: `${height}px`,
          minHeight: '300px',
        }}
      />

      {/* Sector insights */}
      <div
        style={{
          marginTop: '16px',
          padding: '12px 16px',
          backgroundColor: '#f8fafc',
          borderRadius: '8px',
          border: '1px solid #e2e8f0',
        }}
      >
        <div
          style={{
            fontSize: '14px',
            fontWeight: '600',
            color: '#1f2937',
            marginBottom: '8px',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
          }}
        >
          <Icon name="factory" size="xs" />{' '}
          {t('charts.sectorComposition.summary.title')}
        </div>
        <div
          style={{
            fontSize: '13px',
            color: '#4b5563',
            lineHeight: '1.4',
          }}
        >
          •{' '}
          <strong>
            {t('charts.sectorComposition.summary.dominantSector')}:
          </strong>{' '}
          {dominantSector} (
          {((Math.max(sector1, sector2, sector3) / total) * 100).toFixed(1)}%)
          <br />• <strong>{t('sectors.primary.short')}:</strong>{' '}
          {sector1.toLocaleString()}{' '}
          {t('charts.sectorComposition.summary.jobs')} (
          {((sector1 / total) * 100).toFixed(1)}%) -{' '}
          {t('sectors.primary.description')}
          <br />• <strong>{t('sectors.secondary.short')}:</strong>{' '}
          {sector2.toLocaleString()}{' '}
          {t('charts.sectorComposition.summary.jobs')} (
          {((sector2 / total) * 100).toFixed(1)}%) -{' '}
          {t('sectors.secondary.description')}
          <br />• <strong>{t('sectors.tertiary.short')}:</strong>{' '}
          {sector3.toLocaleString()}{' '}
          {t('charts.sectorComposition.summary.jobs')} (
          {((sector3 / total) * 100).toFixed(1)}%) -{' '}
          {t('sectors.tertiary.description')}
          <br />•{' '}
          <strong>
            {t('charts.sectorComposition.summary.economicCharacter')}:
          </strong>{' '}
          {sector3 / total > 0.7
            ? t('charts.sectorComposition.summary.highlyServiceOriented')
            : sector2 / total > 0.4
              ? t('charts.sectorComposition.summary.strongIndustrialBase')
              : sector1 / total > 0.3
                ? t('charts.sectorComposition.summary.primarySectorEconomy')
                : t('charts.sectorComposition.summary.balancedDiversified')}
        </div>
      </div>
    </div>
  )
})

SectorCompositionChart.displayName = 'SectorCompositionChart'
